import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './CreatorModal.module.css';
import {
    AdminManager,
    AdminRetrieveInfluencer,
    deleteAdminInfluencerCost,
    getAdminRetrieveInfluencer,
    InfluencerCost,
    isCampaignType,
    isPlatform,
    patchAdminInfluencerCost,
    patchAdminRetrieveInfluencer,
    postAdminInfluencerCost,
    getAdminManager,
    WriteAdminInfluencerResponse,
    ApexCreatorAgency,
    getApexCreatorAgency,
} from '@round/api';
import omit from 'lodash/omit';
import useNonNullContext from '../../../../hooks/useNonNullContext';
import { OptionsContext } from '../../../../contexts/OptionsContext';
import { useInfluencerTags } from '../../../../hooks/useInfluencerTags';
import useAbortableEffect from '../../../../hooks/useAbortableEffect';
import { communityManagerOption, paymentTermOptions, preferredPaymentOptions } from '../../../../utils/constants';
import { FormikHelpers } from 'formik';
import isEqual from 'lodash/isEqual';
import { transform } from 'lodash';
import fromPairs from 'lodash/fromPairs';
import differenceWith from 'lodash/differenceWith';
import toPairs from 'lodash/toPairs';
import { mapApiErrorsToFormikErrors } from '../../../../utils/utits';
import { showNotification } from '../../../../utils/showNotification';
import { Modal, Spinner } from '@round/ui-kit';
import cn from 'classnames';
import AdminCreatorForm, { AdminCreatorFormValues } from './AdminCreatorForm/AdminCreatorForm';
import { InfluencerCostFormFields } from '../../../creator/creator.types';
import { useContentTags } from '../../../../contexts/ContentTagsContext';

type EditAdminCreatorModalProps = {
    influencerId: number | null;
    influencerCosts: InfluencerCost[];
    isOpen: boolean;
    onClose: () => void;
    onUpdated: (data: { influencer: WriteAdminInfluencerResponse | null; costsChanged: boolean }) => void;
};

const getInfluencerData = (values: AdminCreatorFormValues): Omit<AdminCreatorFormValues, 'costs'> =>
    omit(values, 'costs');

const EditAdminCreatorModal = ({
    isOpen,
    onClose,
    onUpdated,
    influencerId,
    influencerCosts,
}: EditAdminCreatorModalProps) => {
    const [influencer, setInfluencer] = useState<AdminRetrieveInfluencer | null>(null);
    const [manager, setManager] = useState<AdminManager | null>(null);
    const [creatorAgency, setCreatorAgency] = useState<ApexCreatorAgency | null>(null);
    const [isLoadingFormData, setIsLoadingFormData] = useState(false);
    const { currencies } = useNonNullContext(OptionsContext);
    const { influencerTags } = useInfluencerTags();

    const { tags: contentTags, isInitialized: isContentTagsInitialized, init: initContentTags } = useContentTags();
    const contentTagOptions = useMemo(
        () =>
            contentTags.map((tag) => ({
                value: tag.id,
                label: tag.name,
            })),
        [contentTags]
    );

    useAbortableEffect(
        (signal) => {
            if (!isContentTagsInitialized) {
                initContentTags({ signal });
            }
        },
        [isContentTagsInitialized, initContentTags]
    );

    const locationOptions = useMemo(
        () =>
            influencerTags
                .filter((tag) => tag.type === 'LOCATION')
                .map((tag) => ({
                    value: tag.id,
                    label: tag.name,
                })),
        [influencerTags]
    );

    const currencyOptions = useMemo(
        () =>
            currencies.map((curr) => ({
                value: curr.id,
                label: curr.name,
            })),
        [currencies]
    );

    const initFormData = useCallback(
        async (requestInit: RequestInit) => {
            if (!influencerId) {
                return;
            }
            try {
                setIsLoadingFormData(true);
                const influencerResponse = await getAdminRetrieveInfluencer(influencerId, requestInit);
                if (influencerResponse.status === 404) {
                    throw new Error("Couldn't get influencer");
                }

                setInfluencer(influencerResponse.data);

                if (influencerResponse.data.manager === null) {
                    setManager(null);
                } else {
                    const managerResponse = await getAdminManager(influencerResponse.data.manager, requestInit);
                    if (managerResponse.status === 404) {
                        throw new Error(`Couldn't get manager`);
                    }

                    setManager(managerResponse.data);
                }

                if (influencerResponse.data.roster_share_agency) {
                    const creatorAgencyResponse = await getApexCreatorAgency(
                        influencerResponse.data.roster_share_agency,
                        requestInit
                    );
                    if (creatorAgencyResponse.status === 404) {
                        throw new Error('Could not get creator agency');
                    }

                    setCreatorAgency(creatorAgencyResponse.data);
                }
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                if (e instanceof Error) {
                    showNotification(e.message, 'error');
                }
            } finally {
                setIsLoadingFormData(false);
            }
        },
        [influencerId]
    );

    useAbortableEffect(
        (signal) => {
            initFormData({ signal });
        },
        [initFormData]
    );

    const initialCostValues: InfluencerCostFormFields = useMemo(() => {
        const costValues: InfluencerCostFormFields = {
            TIKTOK: { BRAND: { USD: '', GBP: '', EUR: '' }, AUDIO: { USD: '', GBP: '', EUR: '' } },
            INSTAGRAM: { BRAND: { USD: '', GBP: '', EUR: '' }, AUDIO: { USD: '', GBP: '', EUR: '' } },
            YOUTUBE: { BRAND: { USD: '', GBP: '', EUR: '' }, AUDIO: { USD: '', GBP: '', EUR: '' } },
        };
        influencerCosts.forEach((cost) => {
            const currencyName = currencies.find((curr) => curr.id === cost.currency)?.name;
            if (currencyName) {
                costValues[cost.platform][cost.campaign_type][currencyName] = cost.cost.toString();
            }
        });
        return costValues;
    }, [currencies, influencerCosts]);

    const initialValues: AdminCreatorFormValues = useMemo(() => {
        if (!influencer) {
            return {};
        }
        return {
            name: influencer?.name ?? '',
            tiktok_username: influencer?.tiktok_username ?? '',
            instagram_username: influencer?.instagram_username ?? '',
            youtube_username: influencer?.youtube_username ?? '',
            agency_commission: parseFloat((influencer?.agency_commission * 100).toFixed(2)) ?? '',
            manager_commission: parseFloat((influencer?.manager_commission * 100).toFixed(2)) ?? '',
            content_tags: contentTagOptions.filter((tagOption) => influencer.content_tags.includes(tagOption.value)),
            first_name: influencer.first_name ?? '',
            last_name: influencer.last_name ?? '',
            birthday: influencer.birthday?.split('-').reverse().join('/') ?? '',
            phone: influencer.phone ?? '',
            email: influencer.email ?? '',
            address_line_1: influencer.address_line_1 ?? '',
            address_region: influencer.address_region ?? '',
            address_city: influencer.address_city ?? '',
            address_postcode: influencer.address_postcode ?? '',
            location: locationOptions.find((tag) => influencer.location === tag.value),
            bank_name: influencer.bank_name ?? '',
            bank_account_number: influencer.bank_account_number ?? '',
            bank_sort_code: influencer.bank_sort_code ?? '',
            payment_terms: paymentTermOptions.find((terms) => influencer.payment_terms === terms.value),
            preferred_currency: currencyOptions.find((curr) => influencer.preferred_currency === curr.value),
            costs: initialCostValues,
            is_available_for_booking: influencer.is_available_for_booking,
            paypal_email_address: influencer.paypal_email_address,
            preferred_payment_method: preferredPaymentOptions.find(
                (o) => o.value === influencer.preferred_payment_method
            ),
            manager: manager ? { value: manager.id, label: manager.username } : communityManagerOption,
            contact_details: influencer.contact_details,
            roster_share_agency: creatorAgency ? { value: creatorAgency.id, label: creatorAgency.name } : null,
        };
    }, [influencer, contentTagOptions, locationOptions, currencyOptions, initialCostValues, manager, creatorAgency]);

    const handleSubmit = useCallback(
        async (values: AdminCreatorFormValues, { setSubmitting, setErrors }: FormikHelpers<AdminCreatorFormValues>) => {
            if (!influencerId) {
                return;
            }

            try {
                setSubmitting(true);
                const influencerData = getInfluencerData(values);
                const influencerDataChanged = !isEqual(getInfluencerData(initialValues), influencerData);
                let updatedInfluencer: WriteAdminInfluencerResponse | null = null;
                if (influencerDataChanged) {
                    const changedInfluencerData: Omit<AdminCreatorFormValues, 'costs'> = transform(
                        fromPairs(differenceWith(toPairs(influencerData), toPairs(initialValues), isEqual)),
                        (acc, value, key) => {
                            // when not replaced by null, username fields trigger validation error and can't be erased
                            if (
                                ['tiktok_username', 'instagram_username', 'youtube_username'].includes(key) &&
                                typeof value === 'string' &&
                                !value
                            ) {
                                // @ts-ignore
                                acc[key] = null;
                                return;
                            }

                            // @ts-ignore
                            acc[key] = value;
                        }
                    );
                    const managerCommissionAsDecimal =
                        typeof changedInfluencerData.manager_commission === 'number'
                            ? changedInfluencerData.manager_commission / 100
                            : null;
                    const agencyCommissionAsDecimal =
                        typeof changedInfluencerData.agency_commission === 'number'
                            ? changedInfluencerData.agency_commission / 100
                            : null;
                    const influencerResponse = await patchAdminRetrieveInfluencer(influencerId, {
                        ...omit(changedInfluencerData, [
                            'manager_commission',
                            'agency_commission',
                            'content_tags',
                            'location',
                            'birthday',
                            'payment_terms',
                            'preferred_currency',
                            'preferred_payment_method',
                            'manager',
                            'roster_share_agency',
                        ]),
                        ...(typeof managerCommissionAsDecimal === 'number'
                            ? { manager_commission: managerCommissionAsDecimal }
                            : {}),
                        ...(typeof agencyCommissionAsDecimal === 'number'
                            ? { agency_commission: agencyCommissionAsDecimal }
                            : {}),
                        content_tags: changedInfluencerData.content_tags
                            ? changedInfluencerData.content_tags?.map((tag) => Number(tag.value))
                            : influencer?.content_tags,
                        location:
                            changedInfluencerData.location !== undefined
                                ? changedInfluencerData.location?.value ?? null
                                : influencer?.location,
                        birthday:
                            typeof changedInfluencerData.birthday === 'string'
                                ? changedInfluencerData.birthday.length
                                    ? changedInfluencerData.birthday.split('/').reverse().join('-')
                                    : null
                                : influencer?.birthday,
                        ...(changedInfluencerData.payment_terms
                            ? { payment_terms: changedInfluencerData.payment_terms.value }
                            : {}),
                        ...(changedInfluencerData.preferred_currency
                            ? { preferred_currency: changedInfluencerData.preferred_currency?.value ?? null }
                            : {}),
                        ...(changedInfluencerData.preferred_payment_method
                            ? {
                                  preferred_payment_method: changedInfluencerData.preferred_payment_method?.value,
                              }
                            : {}),
                        ...(changedInfluencerData.manager ? { manager: changedInfluencerData.manager.value } : {}),
                        ...{ roster_share_agency: changedInfluencerData.roster_share_agency?.value ?? null },
                    });
                    if (influencerResponse.status === 400) {
                        setErrors(mapApiErrorsToFormikErrors(influencerResponse.data));
                        return;
                    }

                    if (influencerResponse.status === 200) {
                        updatedInfluencer = influencerResponse.data;
                    }
                }

                const requests = [];
                for (const platform in values.costs) {
                    for (const campaignType in values.costs[platform]) {
                        for (const cost in values.costs[platform][campaignType]) {
                            if (
                                values.costs[platform][campaignType][cost] ===
                                initialCostValues[platform][campaignType][cost]
                            ) {
                                continue;
                            }

                            const currencyId = currencies.find((curr) => curr.name === cost.toUpperCase())?.id;
                            if (!currencyId) {
                                throw new Error('Couldnt find requested currency');
                            }

                            const influencerCostId = influencerCosts.find(
                                (cost) =>
                                    cost.influencer === influencerId &&
                                    cost.platform === platform &&
                                    cost.campaign_type === campaignType &&
                                    cost.currency === currencyId
                            )?.id;

                            if (isPlatform(platform) && isCampaignType(campaignType)) {
                                if (influencerCostId) {
                                    if (values.costs[platform][campaignType][cost] === '') {
                                        requests.push(deleteAdminInfluencerCost(influencerCostId));
                                    } else {
                                        requests.push(
                                            patchAdminInfluencerCost(influencerCostId, {
                                                cost: Number(values.costs[platform][campaignType][cost]),
                                            })
                                        );
                                    }
                                } else {
                                    requests.push(
                                        postAdminInfluencerCost({
                                            influencer: influencerId,
                                            cost: Number(values.costs[platform][campaignType][cost]),
                                            campaign_type: campaignType,
                                            platform: platform,
                                            currency: currencyId,
                                        })
                                    );
                                }
                            } else {
                                throw new Error('supplied form values did not meet type specification');
                            }
                        }
                    }
                }

                const results = (await Promise.allSettled(requests)).filter(
                    (result): result is PromiseFulfilledResult<any> => result.status === 'fulfilled'
                );

                onClose();
                onUpdated({ costsChanged: results.length > 0, influencer: updatedInfluencer });
            } catch {
                showNotification('Could not perform operation', 'error');
            } finally {
                setSubmitting(false);
            }
        },
        [
            onClose,
            onUpdated,
            influencerId,
            initialValues,
            influencerCosts,
            initialCostValues,
            currencies,
            influencer?.content_tags,
            influencer?.location,
            influencer?.birthday,
        ]
    );

    useEffect(() => {
        if (!isOpen) {
            setInfluencer(null);
            setManager(null);
            setCreatorAgency(null);
        }
    }, [isOpen]);

    return (
        <Modal
            className={cn(styles.modal, { [styles.loading]: isLoadingFormData })}
            isOpen={isOpen}
            onClose={onClose}
            closeOnOverlayClick
        >
            {isLoadingFormData && (
                <div className={styles.loadingOverlay}>
                    <Spinner />
                </div>
            )}
            <AdminCreatorForm handleSubmit={handleSubmit} initialValues={initialValues} />
        </Modal>
    );
};

export default EditAdminCreatorModal;
